import { useState, useCallback } from 'react'

import { initRequestParam } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'
import { useApiCacheStore } from 'store/apiCacheStore'
import { normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'
// import { useCurrentStatusStore } from 'store/currentStatusStore'

import type { Customer, GetMstCustomersCustomerGetRequest, MasterCustomerUpdateMasterCustomerUpdatePostRequest, MstCustomer, ResponseResult, ResponseUpdate } from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface AutocompleteCustomer {
  label: string | null
  customerCompanyId: number | null
  orgCustomerId: string | null
}

export interface MstCustomerSeq extends MstCustomer{
  id: string
}

export interface MstCustomersSeq extends Customer{
  list: MstCustomerSeq[]
}

export const useCustomer = () => {
  const [customers, setCustomers] = useState<MstCustomersSeq>(initRequestParam)
  const [autocompletCustomers, setAutocompleteCustomers] = useState<AutocompleteCustomer[]>([])

  const [hasCompanyIdCustomers, setHasCompanyIdCustomers] = useState<MstCustomer[] | []>([])
  const [autocompletHasCompanyICustomers, setAutocompleteHasCompanyICustomers] = useState<AutocompleteCustomer[]>([])
  const [result, setResult] = useState<ResponseResult | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateResult, setUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const [customerSearchResult, setCustomerSearchResult] = useState<ResponseResult | undefined>(undefined)
 
  // const { setCurrentStatuses } = useCurrentStatusStore()
  const { callApiWithCache } = useApiCacheStore()
  const { loginUserInfo } = useAuthContext()
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  const fetchCustomer = useCallback(async (searchParam: GetMstCustomersCustomerGetRequest, forceRefresh = false) => {
    setCustomers(initRequestParam)
    setCustomerSearchResult(undefined)
    setIsLoading(true)

    try {
      const response = await callApiWithCache(
        '/customer',
        searchParam,
        async () => {
          const api = await getAPI()
          if (!api) throw new Error('API initialization failed')
          return api.getMstCustomersCustomerGet(searchParam)
        },
        systemDate,
        forceRefresh
      )

      setCustomers({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      const customer = _.map(response.data.list, (value) => ({
        label: value.customerName,
        customerCompanyId: null,
        orgCustomerId: value.orgCustomerId
      }))
      setAutocompleteCustomers(customer)

      // customer_company_idが入っているデータのみ抽出
      const hasCustomerCompanyId = _.filter(response.data.list, (value) => value.customerCompanyId != null)
      setHasCompanyIdCustomers(hasCustomerCompanyId)

      const hasCustomerCompanyIdMap = _.map(hasCustomerCompanyId, (value) => ({
        label: value.customerName,
        customerCompanyId: value.customerCompanyId,
        orgCustomerId: value.orgCustomerId
      }))
      setCustomerSearchResult(response.result)
      setAutocompleteHasCompanyICustomers(hasCustomerCompanyIdMap)
      // setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
    } catch (error) {
      const errorResponse = error as ErrorResponse
      setResult({
        status: 'error',
        message: errorResponse.response && errorResponse.response.status === 422 ? '入力値に誤りがあります。' : `得意先マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    } finally {
      setIsLoading(false)
    }
  }, [callApiWithCache])

  const fetchCustomerUpdate = async (body: MasterCustomerUpdateMasterCustomerUpdatePostRequest): Promise<ResponseUpdate> => {
      setIsUpdating(true);
      setResult(undefined)
      // setCenterUpdateIsLoading(true);
      try {
        const api = await getAPI()
        if (!api) throw new Error('API initialization failed')
        const response = await api.masterCustomerUpdateMasterCustomerUpdatePost(body);
        
        setResult(response.result)
        setUpdateResult(response);
  
        if (response.result.status !== 'success') {
          const error = new Error(response.result.message || '入力エラーがあります');
          Object.assign(error, {
              ...response
          });
          throw error;
        }

        // Clear cache for customer after successful update
        const { clearCache } = useApiCacheStore.getState()
        clearCache('/customer')
        
        return response;
      } catch (error) {
          if (error && typeof error === 'object' && 'result' in error) {
              const apiResponse = error as ResponseUpdate; 
              await new Promise<void>(resolve => {
                setUpdateResult(apiResponse);
                resolve();
              });
              return await Promise.reject(apiResponse);
          }
          const { response } = error as any;
          const errorResponse = {
              errors: [],
              result: {
                  status: 'error',
                  message: response && response.status === 422 ? '入力値に誤りがあります。' : `得意先マスタの更新でエラーが発生しました。`,
                  systemDate: null,
                  pageNo: 0,
                  systemInfo: null,
              },
              updateCount: null,
              lastUpdateDatetime: null,
          };
          await new Promise<void>(resolve => {
            setUpdateResult(errorResponse);
              setResult(errorResponse.result)
              resolve();
          });
          return await Promise.reject(errorResponse);
  
      } finally {
        // setCenterUpdateIsLoading(false);
        setIsUpdating(false);
      }
    };

  return {
    customers,
    autocompletCustomers,

    hasCompanyIdCustomers,
    autocompletHasCompanyICustomers,
    fetchCustomer,
    customerSearchResult,
    result,
    isLoading,

    fetchCustomerUpdate,
    updateResult,
    isUpdating
  }
}
